import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { INCIDENT_CATEGORY_LABELS } from '../lib/constants.js';

export function IncidentReportView({ currentUser, locations }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  // F-201-C fix: staff + managers can only file at locations they're
  // assigned to. Owners see all cafés (they cover the chain).
  const isOwner = /^(owner|admin|administrator)$/i.test(currentUser.role || '');
  const myLocIds = (() => {
    if (isOwner) return null;
    let ids = [];
    try { ids = JSON.parse(currentUser.location_ids || '[]'); } catch (_) {}
    if ((!ids || ids.length === 0) && currentUser.location_id) ids = [currentUser.location_id];
    if (currentUser.primary_location_id && !ids.includes(currentUser.primary_location_id)) {
      ids.push(currentUser.primary_location_id);
    }
    return ids.map(Number);
  })();
  const scopedLocations = isOwner ? (locations || []) :
    (locations || []).filter((l) => myLocIds.includes(l.id));
  const defaultLocId = currentUser.primary_location_id || currentUser.location_id ||
    (scopedLocations && scopedLocations[0]?.id) || '';
  // Default occurred_at to "now", formatted for <input type="datetime-local">
  // (YYYY-MM-DDTHH:mm, local timezone).
  function nowLocalIso() {
    const d = new Date();
    const tz = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tz).toISOString().slice(0, 16);
  }
  const [form, setForm] = useState({
    category: 'injury', severity: 'normal',
    location_id: defaultLocId,
    occurred_at: nowLocalIso(),
    title: '', description: '', is_anonymous: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState('');
  const [mine, setMine] = useState([]);

  async function loadMine() {
    try {
      const r = await api('GET', '/api/me/incidents', null, headers);
      setMine(Array.isArray(r?.incidents) ? r.incidents : []);
    } catch (_) { setMine([]); }
  }
  useEffect(() => { loadMine(); }, []);

  async function submit() {
    if (!form.description.trim()) { setMsg('Please describe what happened.'); return; }
    if (!form.location_id) { setMsg('Pick a location.'); return; }
    setSubmitting(true); setMsg('');
    try {
      await api('POST', '/api/incidents', {
        category: form.category,
        severity: form.severity,
        location_id: Number(form.location_id),
        occurred_at: form.occurred_at
          ? new Date(form.occurred_at).toISOString()
          : null,
        title: form.title.trim() || null,
        description: form.description.trim(),
        is_anonymous: !!form.is_anonymous,
      }, headers);
      setMsg('Submitted. Managers have been notified.');
      setForm((f) => ({ ...f, title: '', description: '', is_anonymous: false, occurred_at: nowLocalIso() }));
      await loadMine();
    } catch (e) {
      setMsg(`Failed: ${e.message || e}`);
    } finally { setSubmitting(false); }
  }

  function fmtWhen(s) {
    try { return new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
    catch { return s; }
  }

  return (
    <div className="space-y-4">
      <div className="card">
        <div className="text-base font-semibold mb-1">🚨 Report an incident</div>
        <div className="text-xs text-gray-500 mb-3">
          Use this to report an injury, a disruptive guest or police-intervention event, broken equipment,
          a customer complaint, theft, or anything else managers should know about. Submissions are sent
          to managers and the owner immediately. For life-threatening emergencies, call 911 first.
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
          <label style={{ fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4 }}>
            Category
            <select value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: 8, fontSize: 13 }}>
              {Object.entries(INCIDENT_CATEGORY_LABELS).map(([v, l]) =>
                <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4 }}>
            Severity
            <select value={form.severity}
              onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}
              style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: 8, fontSize: 13 }}>
              <option value="high">High — needs attention today</option>
              <option value="normal">Normal — log for review</option>
            </select>
          </label>
          <label style={{ fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4 }}>
            Location
            <select value={form.location_id}
              onChange={(e) => setForm((f) => ({ ...f, location_id: e.target.value }))}
              disabled={!isOwner && scopedLocations.length <= 1}
              style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: 8, fontSize: 13,
                        background: (!isOwner && scopedLocations.length <= 1) ? '#f5f5f5' : '#fff' }}>
              {scopedLocations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            When did this happen?
            <input type="datetime-local" value={form.occurred_at}
              onChange={(e) => setForm((f) => ({ ...f, occurred_at: e.target.value }))}
              style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 8, fontSize: 13, boxSizing: 'border-box' }} />
          </label>
        </div>

        <label style={{ fontSize: 12, color: '#555', display: 'block', marginTop: 10, marginBottom: 4 }}>Short title (optional)</label>
        <input type="text" value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          placeholder="e.g. Customer slipped near front door"
          style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 10, fontSize: 14, boxSizing: 'border-box' }} />

        <label style={{ fontSize: 12, color: '#555', display: 'block', marginTop: 10, marginBottom: 4 }}>What happened? *</label>
        <textarea value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          rows={6}
          placeholder="Describe what happened, who was involved, and any immediate actions taken."
          style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 10, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }} />

        <label style={{ fontSize: 13, color: '#555', display: 'flex', alignItems: 'center', gap: 8, marginTop: 10,
                          padding: '8px 12px', background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 6 }}>
          <input type="checkbox" checked={form.is_anonymous}
            onChange={(e) => setForm((f) => ({ ...f, is_anonymous: e.target.checked }))} />
          <span>Submit anonymously — your name won't be attached to this report.</span>
        </label>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center', marginTop: 12 }}>
          {msg && <span style={{ fontSize: 12, color: msg.startsWith('Failed') ? '#b00020' : '#2e7d32' }}>{msg}</span>}
          <button onClick={submit} disabled={submitting}
            style={{ padding: '10px 20px', border: 0, background: '#b91c1c', color: '#fff',
                      fontWeight: 700, borderRadius: 8, fontSize: 14, cursor: 'pointer' }}>
            {submitting ? 'Submitting…' : 'Submit report'}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="text-sm font-semibold mb-2">Your past reports</div>
        <div className="text-xs text-gray-500 mb-2">
          You'll see only that your report was submitted and whether it's been resolved.
          Manager notes and triage details are kept private.
        </div>
        {mine.length === 0 ? (
          <div className="text-xs text-gray-500" style={{ fontStyle: 'italic' }}>
            You haven't filed any reports yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {mine.map((r) => (
              <div key={r.id} style={{ border: '1px solid #e0e0e0', borderRadius: 6, padding: 10,
                                          display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, fontSize: 13 }}>
                <span>
                  {INCIDENT_CATEGORY_LABELS[r.category] || r.category}
                  <span style={{ marginLeft: 8, fontSize: 11, color: '#888' }}>{fmtWhen(r.created_at)}</span>
                </span>
                <span style={{ fontSize: 11, padding: '2px 10px', borderRadius: 99,
                                  background: r.resolved ? '#e6f4ea' : '#fef9ee',
                                  color: r.resolved ? '#1e7d3a' : '#8e6c33',
                                  border: `1px solid ${r.resolved ? '#c8e2c8' : '#f5e6c8'}` }}>
                  {r.resolved ? 'Resolved' : 'Submitted — under review'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
